<template>
  <div class="bg-settings-panel">
    <!-- 实时预览区域 -->
    <div class="real-time-preview" v-if="showRealtimePreview">
      <div class="preview-title">实时预览</div>
      <div
          class="preview-area"
          :style="previewStyle"
      >
        <div class="preview-content">
          <div class="preview-text">示例文本</div>
          <div class="preview-card">示例卡片</div>
        </div>
      </div>
      <div class="preview-info">
        <span v-if="localSettings.type === 'image'">
          图片模糊度: {{ localSettings.blurLevel }}px
          <span class="divider">|</span>
          透明度: {{ Math.round(localSettings.opacity * 100) }}%
        </span>
        <span v-if="localSettings.type === 'color'">
          颜色: {{ localSettings.backgroundColor }}
        </span>
      </div>
    </div>

    <!-- 背景类型选择 -->
    <div class="setting-section">
      <h4>背景类型</h4>
      <el-radio-group v-model="localSettings.type" size="small" @change="handleTypeChange">
        <el-radio-button label="image">图片背景</el-radio-button>
        <el-radio-button label="color">纯色背景</el-radio-button>
        <el-radio-button label="none">无背景</el-radio-button>
      </el-radio-group>
    </div>

    <!-- 图片背景设置 -->
    <div v-if="localSettings.type === 'image'" class="setting-section">
      <h4>背景图片</h4>

      <!-- 预设背景 -->
      <div class="bg-presets">
        <div
            v-for="preset in bgPresets"
            :key="preset.name"
            class="bg-preset-item"
            :style="{ backgroundImage: `url(${preset.url})` }"
            @click="selectBgPreset(preset)"
            :class="{ selected: localSettings.imageUrl === preset.url }"
        >
          <div class="preset-name">{{ preset.name }}</div>
          <div v-if="localSettings.imageUrl === preset.url" class="selected-indicator">
            <i class="el-icon-check"></i>
          </div>
        </div>
      </div>

      <!-- 自定义背景 -->
      <div class="custom-bg-upload">
        <el-upload
            action="#"
            :show-file-list="false"
            :before-upload="() => false"
            :on-change="handleBgImageUpload"
            accept="image/*"
            :disabled="uploadLoading"
        >
          <el-button
              type="primary"
              size="small"
              plain
              icon="el-icon-upload"
              :loading="uploadLoading"
          >
            上传自定义背景
          </el-button>
        </el-upload>
        <p class="upload-tip">支持 JPG/PNG 格式，最大 5MB</p>

        <!-- 当前背景预览 -->
        <div v-if="localSettings.customImage" class="current-custom-bg">
          <div class="preview-title">当前自定义背景：</div>
          <div class="custom-bg-preview" :style="{ backgroundImage: `url(${localSettings.customImage})` }"></div>
          <el-button
              type="text"
              size="mini"
              @click="removeCustomImage"
              class="remove-custom-btn"
          >
            移除自定义背景
          </el-button>
        </div>
      </div>

      <!-- 模糊度调节 -->
      <div class="setting-section">
        <h4>
          模糊度调节
          <span class="blur-value">{{ localSettings.blurLevel }}px</span>
        </h4>
        <div class="blur-controls">
          <el-slider
              v-model="localSettings.blurLevel"
              :min="0"
              :max="20"
              :step="1"
              show-input
              input-size="small"
              @input="handleBlurChange"
          ></el-slider>

          <!-- 快速设置按钮 -->
          <div class="quick-blur-buttons">
            <el-button
                v-for="level in blurPresets"
                :key="level.value"
                size="mini"
                @click="setQuickBlur(level.value)"
                :type="localSettings.blurLevel === level.value ? 'primary' : ''"
                plain
            >
              {{ level.label }}
            </el-button>
          </div>
        </div>
      </div>

      <!-- 透明度调节 -->
      <div class="setting-section">
        <h4>
          透明度调节
          <span class="opacity-value">{{ Math.round(localSettings.opacity * 100) }}%</span>
        </h4>
        <el-slider
            v-model="localSettings.opacity"
            :min="0.1"
            :max="1"
            :step="0.05"
            show-input
            input-size="small"
            @input="handleOpacityChange"
        ></el-slider>
      </div>
    </div>

    <!-- 纯色背景设置 -->
    <div v-if="localSettings.type === 'color'" class="setting-section">
      <h4>背景颜色</h4>
      <div class="color-picker-container">
        <el-color-picker
            v-model="localSettings.backgroundColor"
            show-alpha
            size="small"
            @change="handleColorChange"
            :predefine="predefineColors"
        ></el-color-picker>
        <el-input
            v-model="localSettings.backgroundColor"
            size="small"
            placeholder="#FFFFFF"
            style="width: 120px; margin-left: 10px;"
            @change="handleColorChange"
        ></el-input>
      </div>

      <!-- 预设颜色 -->
      <div class="color-presets">
        <div
            v-for="(color, index) in colorPresets"
            :key="index"
            class="color-preset-item"
            :style="{ backgroundColor: color.value }"
            @click="selectColorPreset(color.value)"
            :class="{ selected: localSettings.backgroundColor === color.value }"
            :title="color.name"
        >
          <span v-if="localSettings.backgroundColor === color.value" class="color-check">
            <i class="el-icon-check"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- 全局设置选项 -->
    <div class="setting-section">
      <h4>设置选项</h4>
      <div class="global-options">
        <el-checkbox
            v-model="localSettings.applyGlobally"
            @change="handleGlobalChange"
        >
          应用到所有页面
        </el-checkbox>
        <el-tooltip content="开启后，此设置将应用于所有支持背景设置的页面" placement="top">
          <i class="el-icon-info info-icon"></i>
        </el-tooltip>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="setting-actions">
      <el-button
          type="primary"
          size="small"
          @click="applySettings"
          :loading="applying"
          style="width: 100%; margin-bottom: 8px;"
      >
        应用设置
      </el-button>
      <div class="action-buttons">
        <el-button
            type="default"
            size="small"
            @click="resetSettings"
            style="flex: 1; margin-right: 4px;"
        >
          恢复默认
        </el-button>
        <el-button
            type="text"
            size="small"
            @click="closeSettings"
            style="flex: 1; margin-left: 4px;"
        >
          取消
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { bgPresets, defaultBackgroundSettings } from '@/utils/storage'

export default {
  name: 'BackgroundSettingsPanel',
  props: {
    value: {
      type: Object,
      required: true
    },
    pageKey: {
      type: String,
      default: 'global'
    },
    showRealtimePreview: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      applying: false,
      uploadLoading: false,
      blurPresets: [
        { value: 0, label: '无模糊' },
        { value: 5, label: '轻度' },
        { value: 10, label: '中度' },
        { value: 15, label: '重度' },
        { value: 20, label: '最强' }
      ],
      colorPresets: [
        { value: '#f0f2f5', name: '浅灰' },
        { value: '#e8f4ff', name: '淡蓝' },
        { value: '#f0ffe8', name: '淡绿' },
        { value: '#fff8e8', name: '淡橙' },
        { value: '#f9f0ff', name: '淡紫' },
        { value: '#fef0f0', name: '淡红' },
        { value: '#f4f4f5', name: '中灰' },
        { value: '#ffffff', name: '纯白' },
        { value: '#000000', name: '纯黑' }
      ],
      predefineColors: [
        '#f0f2f5',
        '#e8f4ff',
        '#f0ffe8',
        '#fff8e8',
        '#f9f0ff',
        '#fef0f0',
        '#f4f4f5',
        '#ffffff',
        '#000000',
        '#409EFF',
        '#67C23A',
        '#E6A23C',
        '#F56C6C',
        '#909399'
      ]
    }
  },
  computed: {
    bgPresets() {
      return bgPresets
    },
    defaultSettings() {
      return defaultBackgroundSettings
    },
    localSettings: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    previewStyle() {
      const style = {}

      if (this.localSettings.type === 'none') {
        style.backgroundColor = 'transparent'
      } else if (this.localSettings.type === 'color') {
        style.backgroundColor = this.localSettings.backgroundColor
      } else if (this.localSettings.type === 'image') {
        style.backgroundImage = `url(${this.localSettings.imageUrl})`
        style.filter = `blur(${this.localSettings.blurLevel}px)`
        style.opacity = this.localSettings.opacity
      }

      return style
    }
  },
  watch: {
    localSettings: {
      handler(newVal) {
        this.$emit('change', newVal)
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    handleTypeChange() {
      this.$emit('change', { type: this.localSettings.type })
    },

    handleBlurChange(value) {
      this.$emit('change', { blurLevel: value })
    },

    handleOpacityChange(value) {
      this.$emit('change', { opacity: value })
    },

    handleColorChange(value) {
      this.$emit('change', { backgroundColor: value })
    },

    handleGlobalChange(value) {
      this.$emit('change', { applyGlobally: value })
    },

    selectBgPreset(preset) {
      this.localSettings.imageUrl = preset.url
      this.localSettings.customImage = null
      this.$emit('change', {
        imageUrl: preset.url,
        customImage: null
      })
      this.$emit('preset-selected', preset)
    },

    selectColorPreset(color) {
      this.localSettings.backgroundColor = color
      this.$emit('change', { backgroundColor: color })
    },

    async handleBgImageUpload(file) {
      if (!file) return false

      this.uploadLoading = true

      try {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        if (!validTypes.includes(file.type)) {
          this.$message.error('只支持 JPG, PNG, GIF, WebP 格式的图片')
          return false
        }

        const maxSize = 5 * 1024 * 1024
        if (file.size > maxSize) {
          this.$message.error('图片大小不能超过 5MB')
          return false
        }

        const reader = new FileReader()
        return new Promise((resolve, reject) => {
          reader.onload = (e) => {
            this.localSettings.imageUrl = e.target.result
            this.localSettings.customImage = e.target.result
            this.$emit('change', {
              imageUrl: e.target.result,
              customImage: e.target.result
            })
            this.$emit('custom-image-uploaded', e.target.result)
            resolve(true)
          }
          reader.onerror = reject
          reader.readAsDataURL(file.raw)
        })
      } catch (error) {
        console.error('上传图片失败:', error)
        this.$message.error('图片上传失败')
        return false
      } finally {
        this.uploadLoading = false
      }
    },

    removeCustomImage() {
      // 使用安全的确认框，防止未处理的 Promise 拒绝
      this.$confirm('确定要移除自定义背景图片吗？', '提示', {
        type: 'warning',
        confirmButtonText: '移除',
        cancelButtonText: '取消',
        distinguishCancelAndClose: true
      }).then(() => {
        try {
          if (this.bgPresets.length > 0) {
            this.localSettings.imageUrl = this.bgPresets[0].url
          } else {
            this.localSettings.imageUrl = defaultBackgroundSettings.imageUrl
          }
          this.localSettings.customImage = null
          this.$emit('change', {
            imageUrl: this.localSettings.imageUrl,
            customImage: null
          })
          this.$emit('custom-image-removed')
        } catch (error) {
          console.error('移除自定义图片失败:', error)
          this.$message.error('移除失败')
        }
      }).catch((action) => {
        // 用户取消操作，不做任何处理，但防止未处理的 Promise 拒绝
        if (action === 'cancel' || action === 'close') {
          // 什么都不做
        } else {
          console.error('确认框错误:', action)
        }
      })
    },

    setQuickBlur(level) {
      this.localSettings.blurLevel = level
      this.$emit('change', { blurLevel: level })
      this.$emit('quick-blur-set', level)
    },

    async applySettings() {
      this.applying = true
      try {
        await this.$emit('apply', this.localSettings)
      } finally {
        setTimeout(() => {
          this.applying = false
        }, 300)
      }
    },

    resetSettings() {
      this.$emit('reset')
    },

    closeSettings() {
      this.$emit('close')
    }
  }
}
</script>
